import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ContextMenuTrigger } from 'react-contextmenu';
import { DropTarget as dropTarget } from 'react-dnd';
import ReactDataGrid from 'react-data-grid';
import { cloneDeep } from 'lodash';
import DnDItemTypes from '../DynamicDnD/DnDItemTypes';
import DataGridRowRenderer from './DataGridRowRenderer';
import DynamicContextMenu from '../DynamicContextMenu';
import { getContextMenuOptions } from '../MenuTypesOptions';
import { upperCaseIfString } from '../../../util/CommonUtils';

const keyWordTarget = {
  canDrop(props, monitor) {
    if (props.isSingleBase && props.gridCategory === 'BasesDataGrid') {
      const { useQuestions, selectedNode } = monitor.getItem();
      if (
        useQuestions ||
        (selectedNode && selectedNode.rectype && selectedNode.rectype === 'Q')
      ) {
        return false;
      }
      return monitor.getItem() && props.gridCategory;
    }
    return monitor.getItem() && props.gridCategory;
  },

  drop(props, monitor, component) {
    if (props.onGridDrop) {
      props.onGridDrop(
        monitor.getItem(),
        monitor.getItemType(),
        props.gridCategory,
        component.state.rows
      );
    }
  },
};

function collect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
    canDrop: monitor.canDrop(),
  };
}

class DynamicDataGrid extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rows: props.rows ? cloneDeep(props.rows) : [],
      sortDirection: 'NONE',
    };
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.rows !== nextProps.rows) {
      if (this.movedRowId) {
        this.setState(
          {
            rows: nextProps.rows.map(item => ({
              ...item,
              isSelected: item.id === this.movedRowId,
            })),
          },
          () => this.props.handleRowsSelectionChange(this.state.sortDirection)
        );
        this.movedRowId = undefined;
      } else {
        this.setState(
          {
            rows: [...nextProps.rows],
          },
          () => this.props.handleRowsSelectionChange(this.state.sortDirection)
        );
      }
    }
  }

  getSelectedEntries = () => this.state.rows.filter(item => item.isSelected);

  getUnselectedEntries = () => this.state.rows.filter(item => !item.isSelected);

  selectAllEntries = () =>
    this.setState(
      prevState => ({
        rows: prevState.rows.map(item => ({ ...item, isSelected: true })),
      }),
      () => this.props.handleRowsSelectionChange(this.state.sortDirection)
    );

  storeMovedRowId = id => {
    this.movedRowId = id;
  };

  handleRowMouseDown = (e, row) => {
    if (
      e.button === 0 &&
      ((this.props.rowSelectionType === 'Multi' &&
        !e.ctrlKey &&
        !e.shiftKey &&
        !this.state.rows[row.idx].isSelected) ||
        this.props.rowSelectionType === 'Single')
    ) {
      this.setState(
        prevState => ({
          rows: prevState.rows.map((item, index) => ({
            ...item,
            isSelected: index === row.idx,
          })),
        }),
        () => this.props.handleRowsSelectionChange(this.state.sortDirection)
      );
    }
  };

  handleRowMouseUp = (e, row) => {
    if (e.button === 0 && this.props.rowSelectionType === 'Multi') {
      if (e.ctrlKey) {
        const modifiedRows = [...this.state.rows];
        modifiedRows[row.idx] = {
          ...modifiedRows[row.idx],
          isSelected: !modifiedRows[row.idx].isSelected,
        };
        return this.setState(
          {
            rows: [...modifiedRows],
          },
          () => this.props.handleRowsSelectionChange(this.state.sortDirection)
        );
      }
      const selectedIndexes = this.state.rows
        .map((item, index) => (item.isSelected ? index : undefined))
        .filter(index => index !== undefined);
      if (
        e.shiftKey &&
        selectedIndexes.length > 0 &&
        selectedIndexes[0] !== row.idx
      ) {
        return this.setState(
          prevState => ({
            rows: prevState.rows.map((item, index) => ({
              ...item,
              isSelected:
                index >= Math.min(selectedIndexes[0], row.idx) &&
                index <= Math.max(selectedIndexes[0], row.idx),
            })),
          }),
          () => this.props.handleRowsSelectionChange(this.state.sortDirection)
        );
      }
      return this.setState(
        prevState => ({
          rows: prevState.rows.map((item, index) => ({
            ...item,
            isSelected: index === row.idx,
          })),
        }),
        () => this.props.handleRowsSelectionChange(this.state.sortDirection)
      );
    }
    return undefined;
  };

  rowGetter = index => {
    const totalRows = [...this.state.rows];
    for (let i = this.state.rows.length; i < this.props.minimunRows; i += 1) {
      totalRows.push({
        title: '',
        sample: '',
        weighted: '',
        isBlank: true,
      });
    }
    return totalRows[index];
  };

  handleRowUpdated = e => {
    // merge updated row with current row and rerender by setting state
    // Used only to edit cell data: [Gaurav]
    if (
      e.fromRow < this.state.rows.length &&
      e.updated &&
      e.updated[e.cellKey]
    ) {
      const rows = [...this.state.rows];
      if (e.cellKey === 'title') {
        let originalT = rows[e.fromRow].originalTitle.trim();
        const updatedT = e.updated.title.trim();
        if (originalT === updatedT) {
          originalT =
            rows[e.fromRow].dataSmartSearch &&
            rows[e.fromRow].dataSmartSearch.objectsAdded &&
            rows[e.fromRow].dataSmartSearch.objectsAdded[0]
              ? rows[e.fromRow].dataSmartSearch.objectsAdded[0].shortText
              : rows[e.fromRow].originalTitle.trim();
        }
        Object.assign(rows[e.fromRow], e.updated);
        if (e.updated.title) {
          this.props.updateCrosstabTitleHeader({
            originalTitle: originalT,
            updatedTitle: updatedT,
            gridCategory: this.props.gridCategory,
            keyword: rows[e.fromRow].keyword,
          });
          Object.assign(rows[e.fromRow], { originalTitle: e.updated.title });
        }
        if (this.props.onDataUpdate) {
          rows[e.fromRow].isLabelUpdated = true;
          rows[e.fromRow].dataSmartSearch.autocodenode.useLabelOverride = true;
          rows[e.fromRow].dataSmartSearch.autocodenode.labelOverride =
            e.updated.title;
          this.props.onDataUpdate(rows, e.fromRow, e.updated);
        } else {
          this.setState({ rows });
        }
      } else if (!isNaN(e.updated[e.cellKey])) {
        const updatedMidpt = e.updated.midpoint.trim();
        if (updatedMidpt) {
          Object.assign(rows[e.fromRow], e.updated);
        }
        if (this.props.onDataUpdate) {
          rows[e.fromRow].dataSmartSearch.autocodenode.useLabelOverride = true;
          rows[e.fromRow].dataSmartSearch.autocodenode.labelOverride =
            e.updated.title;
          this.props.onDataUpdate(rows, e.fromRow, e.updated);
        } else {
          this.setState({ rows });
        }
      }
    }
  };

  handleGridSort = (sortColumn, sortDirection) => {
    const selectedColumn = this.props.columns.filter(
      column => column.key === sortColumn
    )[0];
    const sortKey = selectedColumn.sortKey
      ? selectedColumn.sortKey
      : sortColumn;
    const comparer = (a, b) => {
      if (sortDirection === 'ASC') {
        return upperCaseIfString(a[sortKey]) > upperCaseIfString(b[sortKey])
          ? 1
          : -1;
      }

      if (sortDirection === 'DESC') {
        return upperCaseIfString(a[sortKey]) < upperCaseIfString(b[sortKey])
          ? 1
          : -1;
      }
      return 0;
    };

    const rows = cloneDeep(this.state.rows.sort(comparer));
    if (sortDirection !== 'NONE') {
      this.setState({ rows, sortDirection });
    } else if (sortDirection === 'NONE') {
      this.setState({ rows: cloneDeep(this.props.rows), sortDirection });
    } else {
      this.setState({ rows, sortDirection }, () =>
        this.props.handleRowsSelectionChange(this.state.sortDirection)
      );
    }
  };

  calculatedColumns = () =>
    this.props.columns.map(column => {
      if (column.width) {
        return {
          ...column,
          width: (column.width / 100) * this.props.gridWidth,
        };
      }

      return { ...column, width: this.props.columns / this.props.gridWidth };
    });

  hoverHandle = (index, props) => {
    if (this.props.hoverHandle) {
      this.props.hoverHandle(index, props);
    }
  };

  render() {
    const {
      connectDropTarget,
      isOver,
      canDrop,
      columns,
      gridWidth,
      gridHeight,
      headerRowHeight,
      rowHeight,
      isCellSelectable,
      className,
      gridCategory,
      minimunRows,
      handleAnswersPreview,
      isRowsDraggable,
      handleContextMenuOptions,
      menuID,
      dataScreen,
    } = this.props;

    const renderDataGrid = () => (
      <ReactDataGrid
        ref={c => {
          this.dataGrid = c;
        }}
        rowKey="id"
        columns={columns}
        rowGetter={this.rowGetter}
        rowsCount={
          this.state.rows.length > minimunRows
            ? this.state.rows.length
            : minimunRows
        }
        rowRenderer={
          <DataGridRowRenderer
            gridCategory={gridCategory}
            dataScreen={dataScreen}
            handleRowMouseDown={this.handleRowMouseDown}
            handleRowMouseUp={this.handleRowMouseUp}
            handleRowDoubleClick={this.props.handleRowDoubleClick}
            handleAnswersPreview={handleAnswersPreview}
            getSelectedEntries={this.getSelectedEntries}
            isRowsDraggable={isRowsDraggable}
            hoverHandle={this.hoverHandle}
          />
        }
        minHeight={gridHeight}
        minWidth={gridWidth}
        headerRowHeight={headerRowHeight}
        rowHeight={rowHeight}
        enableCellSelect={isCellSelectable}
        onGridRowsUpdated={this.handleRowUpdated}
        onGridSort={this.handleGridSort}
        rowSelection={{
          showCheckbox: false,
          selectBy: { isSelectedKey: 'isSelected' },
        }}
        onRowClick={this.props.clickHandler}
      />
    );

    const wrapDataGrid = () => {
      if (menuID) {
        return (
          <div
            className={
              canDrop && isOver ? `${className} gridDroppable` : className
            }
            id={`${this.props.gridCategory}_gridType`}
          >
            <ContextMenuTrigger id={menuID} holdToDisplay={-1}>
              {renderDataGrid()}
            </ContextMenuTrigger>
            <DynamicContextMenu
              menuOptions={getContextMenuOptions(menuID)}
              handleMenuOptions={option =>
                handleContextMenuOptions(option, gridCategory)
              }
              menuID={menuID}
            />
          </div>
        );
      }
      return (
        <div
          className={
            canDrop && isOver ? `${className} gridDroppable` : className
          }
          id={`${this.props.gridCategory}_gridType`}
          ref={`${this.props.gridCategory}_gridType`}
        >
          {renderDataGrid()}
        </div>
      );
    };

    return connectDropTarget(<div>{wrapDataGrid()}</div>);
  }
}

DynamicDataGrid.propTypes = {
  menuID: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(PropTypes.object),
  gridHeight: PropTypes.number,
  gridWidth: PropTypes.number,
  isCellSelectable: PropTypes.bool,
  className: PropTypes.string,
  gridCategory: PropTypes.string,
  rowSelectionType: PropTypes.string,
  handleAnswersPreview: PropTypes.func,
  handleRowDoubleClick: PropTypes.func,
  handleRowsSelectionChange: PropTypes.func,
  handleContextMenuOptions: PropTypes.func,
  updateCrosstabTitleHeader: PropTypes.func,
  onDataUpdate: PropTypes.func,
  connectDropTarget: PropTypes.func,
  isOver: PropTypes.bool,
  canDrop: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  minimunRows: PropTypes.number,
  isRowsDraggable: PropTypes.bool,
  headerRowHeight: PropTypes.number,
  rowHeight: PropTypes.number,
  hoverHandle: PropTypes.func,
  dataScreen: PropTypes.string,
  clickHandler: PropTypes.func,
};

DynamicDataGrid.defaultProps = {
  isCellSelectable: false,
  rowSelectionType: 'None',
  className: '',
  minimunRows: 10,
  isRowsDraggable: true,
  handleRowsSelectionChange: () => {},
  handleContextMenuOptions: () => {},
  handleRowDoubleClick: () => {},
  headerRowHeight: 35,
  rowHeight: 35,
};

export default dropTarget(
  [
    DnDItemTypes.DnD_ITEM_TYPE_1,
    DnDItemTypes.DnD_ITEM_TYPE_2,
    DnDItemTypes.DnD_ITEM_TYPE_3,
    DnDItemTypes.DnD_ITEM_TYPE_4,
    DnDItemTypes.DnD_ITEM_TYPE_7,
  ],
  keyWordTarget,
  collect
)(DynamicDataGrid);
